import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, BookOpen, FileText, ChevronRight, Home } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const RESEARCH_STUDIES = [
  {
    id: 1,
    source: "McKinsey & Company",
    title: "Attracting and Retaining the Right Talent",
    year: "2017",
    summary: "Research on why strategic talent acquisition at the leadership level requires a fundamentally different approach than traditional recruitment. Covers the hidden costs of bad hires and the compounding value of exceptional leadership talent.",
    url: "https://www.mckinsey.com/capabilities/people-and-organizational-performance/our-insights/attracting-and-retaining-the-right-talent",
    relevantModules: [1, 6, 9]
  },
  {
    id: 2,
    source: "McKinsey & Company",
    title: "Mindsets and Practices of Excellent CEOs",
    year: "2019",
    summary: "A comprehensive study of what separates the best CEOs from the rest. Essential reading for consultants presenting to boards and advising on C-suite hires.",
    url: "https://www.mckinsey.com/capabilities/strategy-and-corporate-finance/our-insights/the-mindsets-and-practices-of-excellent-ceos",
    relevantModules: [1, 3, 7]
  },
  {
    id: 3,
    source: "McKinsey & Company",
    title: "The Hidden Value of Organisational Health",
    year: "2014",
    summary: "Why organizational health is as important as financial health — and how leadership quality drives both. Key framework for discussing strategic hires with clients.",
    url: "https://www.mckinsey.com/capabilities/people-and-organizational-performance/our-insights/the-hidden-value-of-organizational-health-and-how-to-capture-it",
    relevantModules: [3, 6, 11]
  },
  {
    id: 4,
    source: "Harvard Business Review",
    title: "Stop Hiring for Culture Fit",
    year: "2018",
    summary: "Patty McCord's influential article on why 'culture fit' is often used as an excuse for bias and homogeneity. Includes practical alternatives for assessing candidate-organization alignment.",
    url: "https://hbr.org/2018/01/how-to-hire",
    relevantModules: [3, 6, 7]
  },
  {
    id: 5,
    source: "McKinsey & Company",
    title: "The CEO Moment: Leadership for a New Era",
    year: "2020",
    summary: "How leadership transitions create critical moments for organizations — and opportunities for search consultants who understand the stakes. Post-pandemic leadership expectations.",
    url: "https://www.mckinsey.com/capabilities/strategy-and-corporate-finance/our-insights/the-ceo-moment-leadership-for-a-new-era",
    relevantModules: [2, 6, 12]
  }
];

export default function ResourcesPage() {
  return (
    <div className="space-y-8" data-testid="resources-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm" aria-label="Breadcrumb">
        <Link to="/" className="flex items-center gap-1 text-slate-500 hover:text-slate-700 transition-colors">
          <Home className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Home</span>
        </Link>
        <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
        <span className="text-slate-900 font-medium">Research & Resources</span>
      </nav>

      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">
          Research & Resources
        </h1>
        <p className="text-slate-500 mt-2">
          Essential research studies and frameworks referenced throughout the Academy. These form the intellectual foundation of modern executive search practice.
        </p>
      </div>

      {/* Research Studies */}
      <div className="space-y-4">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Research Studies</h2>
        
        {RESEARCH_STUDIES.map((study) => (
          <div
            key={study.id}
            className="bg-white border border-slate-200 rounded-sm p-6 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] hover:shadow-md transition-shadow"
            data-testid={`research-study-${study.id}`}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#005d6c]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-[#005d6c]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-[#005d6c] uppercase tracking-wider font-semibold">
                    {study.source}
                  </span>
                  <span className="text-xs text-slate-400">• {study.year}</span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                  {study.title}
                </h3>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                  {study.summary}
                </p>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Referenced in:</span>
                    {study.relevantModules.map((modId) => (
                      <Link
                        key={modId}
                        to={`/modules/${modId}`}
                        className="text-xs px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded transition-colors"
                      >
                        Module {modId}
                      </Link>
                    ))}
                  </div>
                  <a
                    href={study.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#005d6c] hover:text-[#004a56] transition-colors"
                  >
                    Read Study
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Resources */}
      <div className="space-y-4">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Additional Resources</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-sm p-5 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-50 rounded-sm flex items-center justify-center">
                <FileText className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <h3 className="font-medium text-slate-900">Template Library</h3>
                <p className="text-xs text-slate-500">SOPs, checklists, and templates</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Access all SOPs and templates from within each module's "SOPs & Templates" tab.
            </p>
            <Link
              to="/modules/1"
              className="text-sm text-[#005d6c] font-medium hover:text-[#004a56] transition-colors inline-flex items-center gap-1"
            >
              Browse Modules <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-5 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-sm flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-medium text-slate-900">AI Tools Guide</h3>
                <p className="text-xs text-slate-500">Module 21 — Advanced</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Learn how to use Claude, ChatGPT, and Perplexity to accelerate your search practice.
            </p>
            <Link
              to="/modules/21"
              className="text-sm text-[#005d6c] font-medium hover:text-[#004a56] transition-colors inline-flex items-center gap-1"
            >
              Go to Module 21 <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
